import {
  ProjectAssetsModel,
  PresetFontModel,
  AsssetsModel,
  AnimationAllType,
  FontModel,
  FragmentTagsModel,
  FragmentListModel,
} from '@ve-pro/api/aside/type'
import { defineStore } from 'pinia'
import { RatioType, RatioMap, Orientation } from 'video-editor-renderer'
import {
  getAssetList,
  getFontList,
  getPresetFonts,
  getVeditorFragmentTags,
} from '@ve-pro/api/aside'
import { DEFAULT_FONT_FAMILY } from '../tracks/resolve-segment'
import { AnimationResource } from '@ve-pro/enums/asset'
import { handleAddResourceAndReturnRes } from '@ve-pro/ve/aside/config/aside-methods'
import { getAssetsUsage } from '@pt/api/assets'
import { AssetsUsage } from '@pt/api/assets/index.type'
import { getAdvUserInfo } from '@pt/api/user'
import type { UserInfo } from '@pt/types/user.type'
import { AccountType, PermissionKey } from '@pt/enums/user'
import type { AssetGroup } from '@ve-pro/api/asset-group/type'
import { getAssetGroupList } from '@ve-pro/api/asset-group'
import { useTrackStore } from '../tracks'
import { isSegmentGroup } from '../tracks/segment/segment-group'
import { SegmentCollection, RatioTypeUnion } from '@ve-pro/types/track.type'
import { ProjectMode, ProjectTab, ProjectType } from '@ve-pro/enums/project'
import { getCommonConfig } from '@/api/common'
import { LanguageItem } from '@com/components/ai-translate/index.type'
import { getUserPackageData } from './user'
import _ from 'lodash'
import { assetsList } from '@pt/api/assets'
import { ProjectAssets, TemplateModel } from '@ve-pro/api/project/type'
import { LabelInfo } from '@ve-pro/api/template/type'
import { resolveDefaultConfig } from './config'
import { TemplateType, AuditStatus } from '@ve-pro/enums/template'
import { useSave } from '@ve-pro/ve/hotkeys/shortcuts/save/save-action'
import { localLanguage } from '@/common/storage'
import { useReplaceStore } from '../replace'
import { ReplaceMode } from '@ve-pro/enums/replace'
import { voiceList } from '@pt/api/ai'
import { VoiceItem, VoiceExtra } from '@pt/api/ai/index.type'

export const useProjectStore = defineStore('project', {
  state: () => {
    return {
      // 项目类型
      type: ProjectType.Normal as ProjectType,

      // 禁止编辑项目
      isDisabled: false,

      // 新增编辑器模式：默认 ｜ 字幕&配音
      mode: ProjectMode.Default as ProjectMode,
      // 编辑器导航栏tab
      tab: ProjectTab.Creative as ProjectTab,
      // 项目是否初始化成功
      isInited: false,
      // 比例
      ratio: RatioType['9:16'] as RatioTypeUnion,
      ratios: [] as Array<RatioTypeUnion>,
      // 名称
      projectName: '',
      // id
      id: -1,
      // 更新时间
      updateTime: new Date().getTime(),
      // 记录被删除的资源列表(目的为了提供给元件做下资源的有效性判断)
      deletedAssets: [] as (
        | ProjectAssetsModel
        | Pick<ProjectAssetsModel, 'asset_id'>
      )[],
      // 比率适配基准
      orientationAdapter: Orientation.Vertical,
      // 项目封面
      coverUrl: '',

      defaultFont: null as ProjectAssetsModel | null,

      // 文本预设
      presetFonts: [] as PresetFontModel[],
      // 动画
      animations: new Map<
        AnimationResource,
        Map<AnimationAllType, AsssetsModel[]>
      >(),

      // 字体库
      fonts: [] as FontModel[],

      // 自定义字体库
      assetFontList: [] as FontModel[],

      assetsUsage: {
        free_size: 0,
        total_assets: 0,
        total_size: 0,
        used_size: 0,
      } as AssetsUsage,

      // 用户信息
      user: null as UserInfo | null,

      // 资源组数据
      assetGroups: [] as Array<AssetGroup & { loading: boolean }>,

      // 视频项目常用语言
      commonLanguages: ['zh-CN', 'en-US', 'ja-JP', 'ko-KR'],

      languageList: [] as LanguageItem[],

      // 项目支持的语言
      languages: [] as string[],

      sourceLanguage: 'en',

      // project config 用于存储一些项目级别的配置
      configs: resolveDefaultConfig(),
      // 项目需要引用的资源，不可被污染
      // 目前仅消除后的资源会存放在这
      assets: {} as ProjectAssets,

      template: null as TemplateModel | null,

      // 是快速替换
      isReplaceProject: false,

      // 模版标签和行业标签
      veditorLabel: null as LabelInfo | null,

      // 片段标签
      fragmentTags: [] as FragmentTagsModel[],

      hotkeysScope: 'all',

      multiRatioPanel: false,

      // 项目中是否存在未下架的场景
      isFragmentOff: false,

      // 坑位翻译场景列表
      translateFragment: [] as FragmentListModel[],

      // 个人音色库
      voiceList: [] as VoiceItem[],

      voiceExtra: {
        used_slot: 0,
        total_slot: 0,
        processing: 0,
        voice_edit: 0,
      } as VoiceExtra,
    }
  },
  getters: {
    isLayoutTemplateProject(state) {
      if (!state.template) return false

      return state.template.type === TemplateType.Common
    },
    isContentTemplateProject(state) {
      if (!state.template) return false

      return state.template.type === TemplateType.Content
    },
    // 是否通用模版 ve_template 中的type=common 或type=content 来判断
    isCommonTemplateProject(state) {
      if (!state.template) return false

      return (
        state.template.type === TemplateType.Common ||
        state.template.type === TemplateType.Content
      )
    },
    // 是否是普通模版
    isNormalTemplateProject(state) {
      if (!state.template) return false

      return state.template.type === TemplateType.Personal
    },
    // 是否是普通项目
    isNormalProject(state) {
      return state.type === ProjectType.Normal
    },
    // 是否是批量项目
    isBatchProject(state) {
      return state.type === ProjectType.Batch
    },
    isNormalOrBatchProject(state) {
      return (
        state.type === ProjectType.Batch || state.type === ProjectType.Normal
      )
    },
    // 是否内部账号
    isInternalAccount(state) {
      return Boolean(state.user?.account_type === AccountType.Internal)
    },

    // 新增是否字幕&配音模式的状态
    isSubtitleMode(state) {
      return state.mode === ProjectMode.Subtitle
    },
    width(state) {
      return RatioMap[state.ratio][0]
    },
    height(state) {
      return RatioMap[state.ratio][1]
    },
    // 存储空间是否满了
    isFullStorage(state) {
      return (
        state.assetsUsage.free_size < 0 && state.assetsUsage.free_size !== -1
      )
    },

    // 是否允许「批量制作」中组的操作（目前只有只有组）
    isAllowGroup(state) {
      const isAllow = state.user?.permissions.includes(PermissionKey.VeBatch)
      return Boolean(isAllow)
    },

    // 是否有ai的权限
    isAllowAI(state) {
      const isAllow = state.user?.permissions.includes(PermissionKey.AI)
      return Boolean(isAllow)
    },

    // 是否是批量制作项目
    isBatch() {
      // 不断言，会丢失类型
      const segments = useTrackStore().segments as SegmentCollection[]
      return segments.some((segment) => isSegmentGroup(segment))
    },

    // 套餐 [number, number] index 0 为余量，index 1 为总量, 值为-1无限量
    plans(state) {
      return getUserPackageData(state.user)
    },

    // 模版提交审核了
    isAudited(state) {
      return (
        state.template?.audit_status === AuditStatus.Approved ||
        state.template?.audit_status === AuditStatus.WaitReview ||
        state.template?.audit_status === AuditStatus.Review ||
        state.isFragmentOff
      )
    },

    isEnterMultiRatioTab(state) {
      return state.tab === ProjectTab.MultiRatio
    },

    /** 进入语言裂变tab */
    isEnterMultiLanguageTab(state) {
      const replaceStore = useReplaceStore()

      if (this.isReplaceProject) {
        return replaceStore.mode === ReplaceMode.Languages
      }
      return state.tab === ProjectTab.MultiLanguage
    },
  },

  actions: {
    async getFontList() {
      const task = getFontList()
      task.then((res) => {
        const list = res.data.data.list

        this.fonts = list ?? []
      })
      return task
    },

    async getAssetFontList() {
      const task = assetsList({
        page: 1,
        page_size: 1000,
        types: ['font'],
        formats: ['ttf', 'otf'],
      })
      task.then((res) => {
        const list = _.get(res, 'data.data.list') || []
        const returnList: FontModel[] = []
        _.map(list, (item) => {
          const payload = JSON.parse(item.payload)
          returnList.push({
            id: item.id,
            label: payload.postscript_name || '',
            svg: item.cover_url,
            url: item.full_url,
            value: item.id + '|' + payload.postscript_name,
          })
        })
        this.assetFontList = returnList
      })
      return task
    },

    async getDefaultFont() {
      console.time('加载默认字体库耗时')
      const font = this.fonts.find((t) => t.value === DEFAULT_FONT_FAMILY)

      if (typeof font === 'undefined') {
        return Promise.reject('没有找到默认字体库')
      }

      const task = handleAddResourceAndReturnRes(font.id)
      task.then((res) => {
        this.defaultFont = res.data.data.assets?.[0] ?? null

        console.timeEnd('加载默认字体库耗时')
      })

      // if (this.defaultFont !== null) {
      // await fontManager.load({
      //   key: String(this.defaultFont.postscript_name),
      //   url: this.defaultFont.file_url,
      //   fallback: true,
      // })
      // }
      return task
    },

    async getPresetFonts() {
      const res = await getPresetFonts({ page_size: 9999 })

      this.presetFonts = res.data.data.list ?? []
    },

    async getAnimationList(
      resource: AnimationResource,
      type: AnimationAllType,
    ): Promise<AsssetsModel[]> {
      let animation = this.animations.get(resource)

      if (!animation) {
        animation = new Map()
        this.animations.set(resource, animation)
      }

      let result = animation.get(type)
      if (result) {
        return result
      }

      const res = await getAssetList({
        adapter_resource: resource,
        cartoon_types: [type],
        page: 1,
        page_size: 9999,
      })

      const list = res.data.data.list
      if (list) {
        result = list
        animation.set(type, list)
      }

      return result ?? []
    },

    async getAssetsUsage() {
      const res = await getAssetsUsage()

      if (!res.data.code) {
        this.assetsUsage = res.data.data
      }
    },

    async pollAssetsUsage() {
      this.getAssetsUsage().finally(() => {
        setTimeout(() => {
          if (this.assetsUsage.free_size !== -1) {
            this.pollAssetsUsage()
          }
        }, 10000)
      })
    },

    async getUser() {
      const task = getAdvUserInfo()
      task.then((res) => {
        const userInfo = res.data.data as UserInfo
        if (window.posthog && typeof window.posthog.identify === 'function') {
          window.posthog.identify(
            String(userInfo.id), // Required. Replace 'distinct_id' with your user's unique identifier
            {
              email: userInfo.email,
              name: userInfo.username,
              env: process.env.NODE_ENV,
            }, // $set, optional
          )
        }
        this.user = userInfo
      })
      return task
    },

    async getAssetGroups() {
      return getAssetGroupList({
        project_id: this.id,
        order: '-created_time',
        page_size: 9999,
      })
    },

    updateAssetGroups(assetGroups: AssetGroup[]) {
      const oldAssetGroups = this.assetGroups
      this.assetGroups = assetGroups.map((item) => ({
        ...item,
        loading: oldAssetGroups.find((g) => g.id === item.id)?.loading ?? false,
      }))
    },

    // 更新资源组的数据
    async updateAssetGroup() {
      const response = await this.getAssetGroups()

      this.updateAssetGroups(response.data.data.list || [])

      return response
    },

    async getLanguages() {
      try {
        const res = await getCommonConfig({
          names: 'veditor_languages',
        })
        if (!res.data.code) {
          this.languageList = (
            _.get(res, 'data.data.veditor_languages') || []
          ).map((item: any) => {
            const maps = {
              'zh-cn': item.name,
              en: item.name_en,
              ja: item.name_jp,
            }

            return {
              name: maps[localLanguage.get() as keyof typeof maps],
              code: item.code,
            }
          })
        }
      } catch (error) {
        console.error(error)
      }
    },

    async updateProjectConfig() {
      return useSave()
    },

    // 模版标签和行业
    async getVeditorLabel() {
      try {
        const res = await getCommonConfig({
          names: 'veditor',
        })
        if (!res.data.code) {
          this.veditorLabel = res.data.data.veditor
        }
      } catch (error) {
        console.error(error)
      }
    },

    // 片段标签
    async getFragmentTags() {
      try {
        const res = await getVeditorFragmentTags()
        if (!res.data.code) {
          this.fragmentTags = res.data.data.tags ?? []
        }
      } catch (error) {
        console.error(error)
      }
    },

    /**
     * 个人音色库
     */
    async getVoiceList() {
      const res = await voiceList({
        order: '-created_time',
        page_size: 9999,
        page: 1,
      })

      if (!res.data.code) {
        this.voiceList = res.data.data.list ?? []

        this.voiceExtra.processing = res.data.data.extra.processing
        this.voiceExtra.total_slot = res.data.data.extra.total_slot
        this.voiceExtra.used_slot = res.data.data.extra.used_slot
        this.voiceExtra.voice_edit = res.data.data.extra.voice_edit
      }
    },
  },
})
